import React, { useEffect, useState } from 'react';
import smoothscroll from 'smoothscroll-polyfill';
import classnames from 'classnames';
import { Link } from 'gatsby';
import { PageType , Language, StorageCookie } from 'enums/types'
import '@formatjs/intl-pluralrules/polyfill';
import '@formatjs/intl-pluralrules/locale-data/en';
import '@formatjs/intl-pluralrules/locale-data/zh';
import '@formatjs/intl-pluralrules/locale-data/fr';
import '@formatjs/intl-pluralrules/locale-data/it';
import '@formatjs/intl-pluralrules/locale-data/ko';
import '@formatjs/intl-pluralrules/locale-data/pt';
import '@formatjs/intl-pluralrules/locale-data/et';
import '@formatjs/intl-pluralrules/locale-data/nl';
import '@formatjs/intl-pluralrules/locale-data/ru';
import '@formatjs/intl-pluralrules/locale-data/lt';
import Header from 'components/header/header';
import Footer from 'components/footer/footer';
import Cookie from 'components/cookie/cookie';
import Notification from 'components/notification/notification';
import icon from 'components/image/headerIcon';
import { Provider } from 'globalState';
import { setRefTracking, setUTM } from 'helpers/helpers';
import { DataParameters } from 'enums/DataParametersForUtm';
import { routes } from 'languages';
import useIsMobile from 'hooks/useIsMobile';
import * as style from './template.module.scss';

interface Props {
	children: JSX.Element | JSX.Element[];
	originalPath: string;
	pageType: PageType;
	locale: string;
}

enum Direction {
	RTL = 'RTL',
	LTR = 'LTR',
}

const blacklistedPathsForCookie = ['/loans-application-form/', '/privacy-policy-page/', '/swift-deposit-instructions/'];
const blacklistedPathsForHeaderAndFooter = [
	'/loans-application-form/',
	'/open-business-account/',
	'/privacy-policy-page/',
];
const blacklistedPathsForHeaderAndFooterMobile = ['/swift-deposit-instructions/'];
const blacklistedPathsForGeneralUTM = [
	'/open-business-account/',
	'/privacy-policy-page/',
	'/swift-deposit-instructions/',
];
const blacklistedPathsForNoNavigationLogo = ['/privacy-policy-page/', '/swift-deposit-instructions/'];

const Layout = ({ children, originalPath, pageType, locale }: Props) => {
	const isMobile = useIsMobile(576);
	const [showNotification, setIsShownNotification] = useState(false);
	const windowIsDefined = typeof window === 'object';
	const documentIsDefined = typeof document === 'object';
	const localStorageInstance = windowIsDefined ? window.localStorage : null;
	const userCountryCodeByLocation = localStorageInstance?.getItem(StorageCookie.DefaultLanguage) ?? null;

		useEffect(() => {
			const cleanupHandlers: Array<{ element: Element; handler: (event: Event) => void }> = [];

			if (documentIsDefined && document.referrer.length > 0) {
				localStorageInstance?.setItem(DataParameters.REFERRER, document.referrer);
			}

		if (windowIsDefined && documentIsDefined) {
			smoothscroll.polyfill();
			const element = document.getElementById(window.location.hash.replace('#', ''));
			if (element)
				setTimeout(() => {
					window.scrollTo({
						behavior: 'smooth',
						top: element.offsetTop,
					});
				}, 100);

				const anchorLinks = Array.from(document.querySelectorAll('a[href^="#"]'));
				anchorLinks.forEach((item) => {
					const handler = (event: Event) => {
						event.preventDefault();
						const hashTag = (event.currentTarget as HTMLElement).getAttribute('href');
						const target = hashTag ? document.querySelector(hashTag) : null;
						window.scrollTo({
							behavior: 'smooth',
							top: target ? target.offsetTop : 60,
						});
					};
					item.addEventListener('click', handler);
					cleanupHandlers.push({ element: item, handler });
				});
			}

			if (userCountryCodeByLocation === 'US') {
				setIsShownNotification(true);
			}

			const search = windowIsDefined ? window.location.search : '';

			if (
				search.includes(DataParameters.UTM_SOURCE) &&
				!blacklistedPathsForGeneralUTM.includes(originalPath)
			) {
				setUTM();
			}

			if (search.includes(DataParameters.REF)) {
				setRefTracking();
			}

			return () => {
				cleanupHandlers.forEach(({ element, handler }) => {
					element.removeEventListener('click', handler);
				});
			};
		}, [documentIsDefined, localStorageInstance, originalPath, userCountryCodeByLocation, windowIsDefined]);

	return (
		<Provider>
			<div className={style.apiContainer} dir={locale === Language.Arabic ? Direction.RTL : Direction.LTR}>
				{!blacklistedPathsForHeaderAndFooter.includes(originalPath) &&
					!(blacklistedPathsForHeaderAndFooterMobile.includes(originalPath) && isMobile) && (
						<Header showLanguageSwitcher originalPath={originalPath} pageType={pageType} />
					)}
				{(blacklistedPathsForHeaderAndFooter.includes(originalPath) ||
					(blacklistedPathsForHeaderAndFooterMobile.includes(originalPath) && isMobile)) &&
					!blacklistedPathsForNoNavigationLogo.includes(originalPath) && (
						<div className={classnames(style.logoWrapper)}>
							<Link to={routes[locale]['/']} className={style.logo}>
								{icon.logo}
							</Link>
						</div>
					)}
				{children}
				{!blacklistedPathsForHeaderAndFooter.includes(originalPath) &&
					!(blacklistedPathsForHeaderAndFooterMobile.includes(originalPath) && isMobile) && <footer><Footer /></footer>}
				{showNotification && (
					<Notification showNotification={(value: boolean) => setIsShownNotification(value)} />
				)}
				{!blacklistedPathsForCookie.includes(originalPath) && <Cookie />}
			</div>
		</Provider>
	);
};

export default Layout;
